import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { zoomService } from '../../services/zoomService';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const STORAGE_KEY = 'oauth-result-microsoft-teams';

function broadcastResult(data: Record<string, unknown>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function MicrosoftTeamsOAuthCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting your Microsoft Teams account...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setStatus('error');
          setMessage('Authorization cancelled or failed');
          broadcastResult({ type: 'microsoft-teams-oauth', success: false, error: 'Authorization cancelled' });
          setTimeout(() => window.close(), 2000);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Invalid callback parameters');
          broadcastResult({ type: 'microsoft-teams-oauth', success: false, error: 'Invalid callback' });
          setTimeout(() => window.close(), 2000);
          return;
        }

        const stateData = JSON.parse(state);
        const { client_id, user_id, sales_member_id } = stateData;

        const result = await zoomService.handleMicrosoftTeamsOAuthCallback(
          code,
          client_id,
          user_id,
          sales_member_id
        );

        if (result.success) {
          setStatus('success');
          setMessage('Microsoft Teams connected successfully!');
          broadcastResult({ type: 'microsoft-teams-oauth', success: true, connection: result.connection });
          setTimeout(() => window.close(), 1500);
        } else {
          setStatus('error');
          setMessage(result.error || 'Failed to connect Microsoft Teams');
          broadcastResult({ type: 'microsoft-teams-oauth', success: false, error: result.error });
          setTimeout(() => window.close(), 3000);
        }
      } catch (error) {
        setStatus('error');
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setMessage(errorMessage);
        broadcastResult({ type: 'microsoft-teams-oauth', success: false, error: errorMessage });
        setTimeout(() => window.close(), 3000);
      }
    };

    handleCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-extrabold text-gray-800 mb-2 font-display">
              Connecting Microsoft Teams
            </h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-extrabold text-gray-800 mb-2 font-display">
              Success!
            </h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">This window will close automatically...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-extrabold text-gray-800 mb-2 font-display">
              Connection Failed
            </h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">This window will close automatically...</p>
          </>
        )}
      </div>
    </div>
  );
}
